import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, UserPlus, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Checkins() {
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/checkins");
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar check-ins. Tente novamente.");
    }
  };
  useEffect(() => { load(); }, []);

  const convert = async (id) => {
    try {
      await api.post(`/checkins/${id}/convert`);
      toast.success("Hóspede criado. Encontre-o em Hóspedes.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao converter");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este check-in?")) return;
    try { await api.delete(`/checkins/${id}`); toast.success("Removido"); load(); }
    catch { toast.error("Erro ao remover"); }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/checkin`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado para a área de transferência");
    } catch {
      toast.info(url);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Formulário público"
        title="Check-ins online"
        description="Cadastros enviados pelos hóspedes através do link público. Ao validar, converta em hóspede para ligar a uma reserva."
        actions={
          <Button data-testid="btn-copy-checkin-link" onClick={copyLink}
            className="bg-[#D4A652] hover:bg-[#a94c31] text-white">
            <Copy size={16} className="mr-2" /> Copiar link público
          </Button>
        }
      />

      <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4 mb-6 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Link público da página de check-in</div>
          <code data-testid="checkin-public-url" className="text-sm text-[#083A6B] break-all">
            {window.location.origin}/checkin
          </code>
        </div>
      </div>

      <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50 hover:bg-stone-50">
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Documento</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Contato</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Empresa</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Enviado</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`checkin-row-${c.id}`}>
                <TableCell className="font-medium text-stone-800">
                  {c.nome}
                  {c.mensagem && <div className="text-xs text-stone-500 mt-0.5">{c.mensagem}</div>}
                </TableCell>
                <TableCell className="text-stone-600">{c.documento}</TableCell>
                <TableCell>
                  <div className="text-stone-700">{c.telefone || "—"}</div>
                  <div className="text-xs text-stone-500">{c.email || "—"}</div>
                </TableCell>
                <TableCell>{c.empresa || "—"}</TableCell>
                <TableCell>{c.pousada_preferida || "—"}</TableCell>
                <TableCell className="whitespace-nowrap">{new Date(c.created_at).toLocaleDateString("pt-BR")}</TableCell>
                <TableCell>
                  <span className={c.status === "processado" ? "text-[#083A6B] text-xs font-medium" : "text-[#8B6A1F] text-xs font-medium"}>
                    {c.status === "processado" ? "Processado" : "Pendente"}
                  </span>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {c.status !== "processado" && (
                    <Button size="sm" data-testid={`btn-convert-${c.id}`} onClick={() => convert(c.id)}
                      className="mr-2 h-8 bg-[#083A6B] hover:bg-[#052B50] text-white text-xs">
                      <UserPlus size={14} className="mr-1" /> Criar hóspede
                    </Button>
                  )}
                  <button data-testid={`btn-delete-checkin-${c.id}`} onClick={() => remove(c.id)}
                    className="text-stone-400 hover:text-[#D4A652] p-1">
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-stone-500">
                  Nenhum check-in recebido ainda. Compartilhe o link público para começar.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
